<template>
    <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="h-full container mx-auto">
            <div class="flex flex-col h-full w-full p-0 sm:p-8">
                <!-- Breadcrumb -->
                <nav class="text-sm text-gray-600 mb-6 flex">
                    <router-link to="/dashboard"
                        class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                    <span class="mx-1">/</span>
                    <span class="text-blue-900 font-semibold">Post News</span>
                </nav>

                <!-- Header -->
                <div class="header-section bg-white shadow-sm rounded-lg p-6 mb-6">
                    <div class="flex items-center justify-between">
                        <div>
                            <h1 class="text-2xl font-bold text-gray-900 mb-2">News Management</h1>
                            <p class="text-gray-600">Create and manage news articles</p>
                        </div>
                        <div class="flex space-x-3">
                            <base-button v-if="hasPermission('news-create')" @click="showCreateModal = true"
                                class="bg-blue-600 hover:bg-blue-700 text-white">
                                <font-awesome-icon icon="plus" class="mr-2" />
                                Create News
                            </base-button>
                        </div>
                    </div>
                </div>

                <!-- Statistics Dashboard -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
                    <base-card class="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                        <div class="flex items-center">
                            <div class="p-3 bg-white bg-opacity-20 rounded-lg">
                                <font-awesome-icon icon="newspaper" class="text-2xl" />
                            </div>
                            <div class="ml-4">
                                <p class="text-sm opacity-90">Total News</p>
                                <p v-if="isStatisticsLoading"
                                    class="h-9 bg-white bg-opacity-20 animate-pulse mt-1 rounded"></p>
                                <p v-else class="text-2xl font-bold mt-1">{{ statistics.total_news || 0 }}</p>
                            </div>
                        </div>
                    </base-card>

                    <base-card class="bg-gradient-to-r from-green-500 to-green-600 text-white">
                        <div class="flex items-center">
                            <div class="p-3 bg-white bg-opacity-20 rounded-lg">
                                <font-awesome-icon icon="calendar-alt" class="text-2xl" />
                            </div>
                            <div class="ml-4">
                                <p class="text-sm opacity-90">This Month</p>
                                <p v-if="isStatisticsLoading"
                                    class="h-9 bg-white bg-opacity-20 animate-pulse mt-1 rounded"></p>
                                <p v-else class="text-2xl font-bold mt-1">{{ statistics.news_this_month || 0 }}</p>
                            </div>
                        </div>
                    </base-card>

                    <base-card class="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                        <div class="flex items-center">
                            <div class="p-3 bg-white bg-opacity-20 rounded-lg">
                                <font-awesome-icon icon="chart-line" class="text-2xl" />
                            </div>
                            <div class="ml-4">
                                <p class="text-sm opacity-90">This Year</p>
                                <p v-if="isStatisticsLoading"
                                    class="h-9 bg-white bg-opacity-20 animate-pulse mt-1 rounded"></p>
                                <p v-else class="text-2xl font-bold mt-1">{{ statistics.news_this_year || 0 }}</p>
                            </div>
                        </div>
                    </base-card>

                    <base-card class="bg-gradient-to-r from-teal-500 to-teal-600 text-white">
                        <div class="flex items-center">
                            <div class="p-3 bg-white bg-opacity-20 rounded-lg">
                                <font-awesome-icon icon="eye" class="text-2xl" />
                            </div>
                            <div class="ml-4">
                                <p class="text-sm opacity-90">Published</p>
                                <p v-if="isStatisticsLoading"
                                    class="h-9 bg-white bg-opacity-20 animate-pulse mt-1 rounded"></p>
                                <p v-else class="text-2xl font-bold mt-1">{{ statistics.published_count || 0 }}</p>
                            </div>
                        </div>
                    </base-card>

                    <!-- Entity-specific tiles -->
                    <base-card v-for="entityStat in entityStatistics" :key="entityStat.entity"
                        :class="getEntityTileColor(entityStat.entity)">
                        <div class="flex items-center">
                            <div class="p-3 bg-white bg-opacity-20 rounded-lg">
                                <font-awesome-icon icon="building" class="text-2xl" />
                            </div>
                            <div class="ml-4">
                                <p class="text-sm opacity-90">{{ mapEntityName(entityStat.entity) }}</p>
                                <p v-if="isStatisticsLoading"
                                    class="h-9 bg-white bg-opacity-20 animate-pulse mt-1 rounded"></p>
                                <p v-else class="text-2xl font-bold mt-1">{{ entityStat.count || 0 }}</p>
                            </div>
                        </div>
                    </base-card>
                </div>

                <!-- News List -->
                <div class="bg-white shadow-sm rounded-lg flex-auto flex flex-col">
                    <div class="p-6 border-b border-gray-200">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                            <h2 class="text-lg font-semibold text-gray-900">All News</h2>
                            <div class="flex items-center gap-4">
                                <div class="w-full sm:w-auto border rounded-lg px-4 h-8 flex items-center">
                                    <div class="text-sm mr-2">Entity:</div>
                                    <select v-model="filterByEntity" @change="loadNews"
                                        class="outline-none text-sm flex-grow">
                                        <option value="">All</option>
                                        <option value="FCHS">FCHS</option>
                                        <option value="ADPOLY">ADPOLY</option>
                                        <option value="ATHS">ATS</option>
                                        <option value="ADVETI">ADVETI</option>
                                    </select>
                                </div>
                                <search @on-search="onSearch" class-list="px-4 h-8"></search>
                            </div>
                        </div>
                    </div>

                    <!-- Loading State -->
                    <div class="flex-auto flex flex-col" v-if="loading">
                        <table-shimmer :titleHeaders="[
                            '#',
                            'Title',
                            'Description',
                            'Entity',
                            'News Date',
                            'Status',
                            'Created By',
                            'Created',
                            'Actions',
                        ]" :rows="first"></table-shimmer>
                    </div>

                    <!-- Empty State -->
                    <div v-else-if="!loading && newsList.length === 0"
                        class="flex-auto flex justify-center items-center py-12">
                        <div class="text-center max-w-md">
                            <div class="mb-6">
                                <div
                                    class="mx-auto w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                                    <font-awesome-icon icon="newspaper" class="h-12 w-12 text-blue-500" />
                                </div>
                            </div>
                            <h2 class="text-2xl font-semibold text-gray-800 mb-3">No News Found</h2>
                            <p class="text-gray-500 mb-6 leading-relaxed">
                                You haven't created any news articles yet. Click the button below to create your first
                                news article.
                            </p>
                            <button v-if="hasPermission('news-create')" @click="showCreateModal = true"
                                class="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 text-sm font-medium shadow-lg">
                                <font-awesome-icon icon="plus" class="h-4 w-4 mr-2" />
                                Create First News
                            </button>
                        </div>
                    </div>

                    <!-- News Table -->
                    <div v-else class="flex flex-col flex-auto">
                        <div class="flex-auto flex flex-col overflow-x-auto">
                            <div class="inline-block min-w-full py-2">
                                <div class="overflow-x-auto">
                                    <table class="min-w-full text-left text-sm font-light">
                                        <thead class="border-b font-medium dark:border-neutral-500">
                                            <tr>
                                                <th scope="col" class="px-6 py-2 text-gray-400 w-8">
                                                    #
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Title
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Description
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Entity
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    News Date
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Status
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Created By
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Created
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(news, index) in newsList" :key="news.id"
                                                class="border-b border-dashed dark:border-neutral-500 hover:bg-gray-50">
                                                <td class="whitespace-nowrap px-6 py-2 font-medium">
                                                    {{ paginatorInfoWithItems ? paginatorInfoWithItems.firstItem + index
                                                        : index + 1 }}
                                                </td>
                                                <td class="px-6 py-2">
                                                    <a v-if="news.is_published" :href="getNewsUrl(news)" target="_blank"
                                                        rel="noopener noreferrer"
                                                        class="font-medium text-gray-900 hover:text-blue-600 hover:underline transition-colors cursor-pointer">
                                                        {{ news.title }}
                                                    </a>
                                                    <span v-else class="font-medium text-gray-900">
                                                        {{ news.title }}
                                                    </span>
                                                </td>
                                                <td class="px-6 py-2">
                                                    <div class="text-gray-600 max-w-md truncate">
                                                        {{ news.description || 'No description' }}
                                                    </div>
                                                </td>
                                                <td class="px-6 py-2 text-gray-900">
                                                    {{ mapEntityName(news.entity) || 'N/A' }}
                                                </td>
                                                <td class="px-6 py-2 text-gray-900">
                                                    {{ formatDate(news.news_date) }}
                                                </td>
                                                <td class="px-6 py-2">
                                                    <span v-if="news.is_published"
                                                        class="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                        Published
                                                    </span>
                                                    <span v-else
                                                        class="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                                        Draft
                                                    </span>
                                                </td>
                                                <td class="px-6 py-2 text-gray-900">
                                                    {{ news.creator ? news.creator.name : 'N/A' }}
                                                </td>
                                                <td class="px-6 py-2 text-gray-900">
                                                    {{ formatDate(news.created_at) }}
                                                </td>
                                                <td class="px-6 py-2">
                                                    <div class="flex">
                                                        <div @click.stop="viewNews(news)"
                                                            class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                                                            title="View Details">
                                                            <icon-eye
                                                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110"></icon-eye>
                                                        </div>
                                                        <div v-if="canEditNews(news)" @click.stop="editNews(news)"
                                                            class="bg-gray-100 hover:bg-indigo-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                                                            title="Edit">
                                                            <icon-pen
                                                                class="w-4 h-4 transform group-hover:text-indigo-600 group-hover:scale-110"></icon-pen>
                                                        </div>
                                                        <div v-if="hasPermission('news-publish')"
                                                            @click.stop="togglePublish(news)"
                                                            :class="news.is_published ? 'bg-green-100 hover:bg-red-50' : 'bg-gray-100 hover:bg-green-50'"
                                                            class="w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                                                            :title="news.is_published ? 'Unpublish' : 'Publish'">
                                                            <font-awesome-icon
                                                                :icon="news.is_published ? 'eye-slash' : 'eye'"
                                                                :class="news.is_published ? 'text-red-600' : 'text-green-600'"
                                                                class="w-4 h-4 transform group-hover:scale-110"></font-awesome-icon>
                                                        </div>
                                                        <div v-if="canDeleteNews(news)" @click.stop="deleteNews(news)"
                                                            class="bg-gray-100 hover:bg-red-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                                                            title="Delete">
                                                            <icon-trash
                                                                class="w-4 h-4 transform group-hover:text-red-600 group-hover:scale-110"></icon-trash>
                                                        </div>
                                                        <div v-if="news.is_published"
                                                            class="bg-gray-100 hover:bg-purple-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                                                            <rsd-copy-to-clipboard
                                                                :text-to-copy="getNewsUrl(news)"></rsd-copy-to-clipboard>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div class="mt-4 px-6 pb-6"
                            v-if="paginatorInfoWithItems && paginatorInfoWithItems.lastPage > 1">
                            <pagination :paginatorInfo="paginatorInfoWithItems" :setPage="setPage"></pagination>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Create/Edit News Modal -->
        <transition name="fade" mode="out-in">
            <base-full-modal v-if="showCreateModal || editingNews"
                :title="editingNews ? `Edit News - ${editingNews.title}` : 'Create News'" @close="closeModal">
                <news-form-modal :news="editingNews" @close="closeModal" @saved="handleNewsSaved"></news-form-modal>
            </base-full-modal>
        </transition>

        <!-- View News Modal -->
        <transition name="fade" mode="out-in">
            <base-full-modal v-if="viewingNews" :title="viewingNews.title" @close="closeViewModal">
                <div class="text-left p-6">
                    <div v-if="viewingNews.thumbnail" class="mb-6">
                        <img :src="resolveThumbnailUrl(viewingNews.thumbnail)" alt="News thumbnail"
                            class="w-full h-64 object-cover rounded-lg" />
                    </div>
                    <div class="space-y-4">
                        <div>
                            <label class="text-sm font-semibold text-gray-700">Description</label>
                            <p class="mt-1 text-gray-900">{{ viewingNews.description || 'No description' }}</p>
                        </div>
                        <div>
                            <label class="text-sm font-semibold text-gray-700">Content</label>
                            <div class="mt-1 text-gray-900 prose max-w-none"
                                v-html="viewingNews.content || 'No content'"></div>
                        </div>
                        <div class="grid grid-cols-2 gap-4">
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Entity</label>
                                <p class="mt-1 text-gray-900">{{ mapEntityName(viewingNews.entity) || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">News Date</label>
                                <p class="mt-1 text-gray-900">{{ formatDate(viewingNews.news_date) }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Status</label>
                                <p class="mt-1">
                                    <span v-if="viewingNews.is_published"
                                        class="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                        Published
                                    </span>
                                    <span v-else
                                        class="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                        Draft
                                    </span>
                                </p>
                            </div>
                            <div v-if="viewingNews.is_published">
                                <label class="text-sm font-semibold text-gray-700">Published At</label>
                                <p class="mt-1 text-gray-900">{{ formatDate(viewingNews.published_at) }}</p>
                            </div>
                            <div v-if="viewingNews.creator">
                                <label class="text-sm font-semibold text-gray-700">Created By</label>
                                <p class="mt-1 text-gray-900">{{ viewingNews.creator.name || 'N/A' }}</p>
                            </div>
                            <div v-if="viewingNews.publisher">
                                <label class="text-sm font-semibold text-gray-700">Published By</label>
                                <p class="mt-1 text-gray-900">{{ viewingNews.publisher.name || 'N/A' }}</p>
                            </div>
                        </div>
                        <div v-if="hasPermission('news-publish')" class="mt-4 pt-4 border-t border-gray-200">
                            <button @click="togglePublish(viewingNews)"
                                :class="viewingNews.is_published ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'"
                                class="px-4 py-2 text-white rounded-lg transition-colors">
                                <font-awesome-icon :icon="viewingNews.is_published ? 'eye-slash' : 'eye'"
                                    class="mr-2" />
                                {{ viewingNews.is_published ? 'Unpublish' : 'Publish' }} News
                            </button>
                        </div>
                    </div>
                </div>
            </base-full-modal>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import Pagination from '../../components/shared/pagination.vue';
import TableShimmer from '../../components/shared/TableShimmer.vue';
import NewsFormModal from './components/NewsFormModal.vue';
import IconEye from '../../components/icons/IconEye.vue';
import IconPen from '../../components/icons/IconPen.vue';
import IconTrash from '../../components/icons/IconTrash.vue';
import RsdCopyToClipboard from '../../components/shared/RsdCopyToClipboard.vue';

const LIST_NEWS_QUERY = gql`
    query NewsList($first: Int!, $page: Int!, $search: String, $entity: String) {
        newsList(first: $first, page: $page, search: $search, entity: $entity) {
            error
            error_code
            message
            data {
                id
                title
                slug
                description
                content
                thumbnail
                news_date
                entity
                created_by
                is_published
                published_at
                published_by
                creator {
                    id
                    name
                }
                publisher {
                    id
                    name
                }
                created_at
                updated_at
            }
            paginatorInfo {
                currentPage
                lastPage
                perPage
                total
                hasMorePages
                firstItem
                lastItem
            }
        }
    }
`;

const GET_NEWS_STATISTICS = gql`
    query GetNewsStatistics {
        newsStatistics {
            error
            error_code
            message
            data {
                total_news
                news_this_month
                news_this_year
                published_count
                news_by_entity {
                    entity
                    count
                }
            }
        }
    }
`;

export default {
    name: 'PostNewsIndex',
    components: {
        Pagination,
        TableShimmer,
        NewsFormModal,
        IconEye,
        IconPen,
        IconTrash,
        RsdCopyToClipboard,
    },
    data() {
        return {
            newsList: [],
            loading: false,
            isStatisticsLoading: false,
            showCreateModal: false,
            editingNews: null,
            viewingNews: null,
            paginatorInfo: null,
            statistics: {
                total_news: 0,
                news_this_month: 0,
                news_this_year: 0,
                news_by_entity: [],
                published_count: 0,
            },
            first: 10,
            page: 1,
            searchTerm: '',
            filterByEntity: '',
        };
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        userId() {
            return this.$store.getters.userId;
        },
        isSuperAdmin() {
            return this.role === 'superadmin';
        },
        paginatorInfoWithItems() {
            if (!this.paginatorInfo) {
                return null;
            }
            const firstItem = this.paginatorInfo.firstItem || ((this.paginatorInfo.currentPage - 1) * this.paginatorInfo.perPage + 1);
            const lastItem = this.paginatorInfo.lastItem || Math.min(this.paginatorInfo.currentPage * this.paginatorInfo.perPage, this.paginatorInfo.total);
            return {
                ...this.paginatorInfo,
                firstItem,
                lastItem,
            };
        },
        entityStatistics() {
            if (!this.statistics.news_by_entity || !Array.isArray(this.statistics.news_by_entity)) {
                return [];
            }
            // Filter out entities with 0 count and return only those with news
            return this.statistics.news_by_entity.filter(entity => entity && entity.count > 0);
        },
    },
    created() {
        this.loadNews();
        this.loadStatistics();
    },
    methods: {
        hasPermission(crudName) {
            if (this.role == 'superadmin') {
                return true;
            } else {
                const permissions = this.$store.getters.permissions;
                const permissionsArray = permissions.split(",");
                if (permissionsArray.includes(crudName)) {
                    return true;
                } else {
                    return false;
                }
            }
        },
        canEditNews(news) {
            // Superadmin can edit any news (published or not)
            if (this.isSuperAdmin) {
                return true;
            }

            // Check if user has news-update permission
            if (!this.hasPermission('news-update')) {
                return false;
            }

            // Non-superadmin users can only edit:
            // 1. Their own news (created_by matches userId)
            // 2. Unpublished news
            return news.created_by == this.userId && !news.is_published;
        },
        canDeleteNews(news) {
            // Superadmin can delete any news
            if (this.isSuperAdmin) {
                return true;
            }

            // Check if user has news-delete permission
            if (!this.hasPermission('news-delete')) {
                return false;
            }

            // Non-superadmin users can only delete:
            // 1. Their own news (created_by matches userId)
            // 2. Unpublished news
            return news.created_by == this.userId && !news.is_published;
        },
        getNewsUrl(news) {
            if (!news || !news.slug) {
                return '';
            }
            const baseUrl = process.env.VUE_APP_FRONTEND_URL || window.location.origin + '/#';
            return `${baseUrl}news/${news.slug}`;
        },
        async loadNews() {
            this.loading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: LIST_NEWS_QUERY,
                    variables: {
                        first: this.first,
                        page: this.page,
                        search: this.searchTerm || '',
                        entity: this.filterByEntity || '',
                    },
                    fetchPolicy: 'network-only',
                });

                if (data.newsList.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: data.newsList.message,
                    });
                } else {
                    this.newsList = data.newsList.data;
                    this.paginatorInfo = data.newsList.paginatorInfo;
                }
            } catch (error) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: 'Failed to load news',
                });
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        setPage(page) {
            this.page = page;
            this.loadNews();
        },
        async loadStatistics() {
            this.isStatisticsLoading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: GET_NEWS_STATISTICS,
                    fetchPolicy: 'network-only',
                });

                if (data.newsStatistics.error) {
                    console.error('Failed to load statistics:', data.newsStatistics.message);
                } else {
                    this.statistics = data.newsStatistics.data;
                }
            } catch (error) {
                console.error('Failed to load statistics:', error);
            } finally {
                this.isStatisticsLoading = false;
            }
        },
        onSearch(searchTerm) {
            this.searchTerm = searchTerm;
            this.page = 1;
            this.loadNews();
        },
        viewNews(news) {
            this.viewingNews = news;
        },
        editNews(news) {
            this.editingNews = news;
        },
        async deleteNews(news) {
            const result = await this.$swal.fire({
                title: 'Are you sure?',
                text: `You are about to delete "${news.title}". This action cannot be undone!`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'Cancel'
            });

            if (!result.isConfirmed) {
                return;
            }

            try {
                const { data } = await this.$apollo.mutate({
                    mutation: gql`
                        mutation DeleteNews($id: ID!) {
                            deleteNews(id: $id) {
                                error
                                error_code
                                message
                            }
                        }
                    `,
                    variables: {
                        id: news.id,
                    },
                });

                if (!data || !data.deleteNews) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Failed!',
                        text: 'Failed to delete news: Invalid response',
                    });
                    return;
                }

                if (data.deleteNews.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: data.deleteNews.message || 'Failed to delete news',
                    });
                } else {
                    this.$swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success',
                        title: 'News deleted successfully',
                        showConfirmButton: false,
                        timer: 1000,
                        timerProgressBar: true,
                    });
                    this.loadNews();
                    this.loadStatistics();
                }
            } catch (error) {
                console.error('Delete news error:', error);
                let errorMessage = 'Failed to delete news';

                if (error.graphQLErrors && error.graphQLErrors.length > 0 && error.graphQLErrors[0].message) {
                    errorMessage = error.graphQLErrors[0].message;
                } else if (error.networkError && error.networkError.message) {
                    errorMessage = error.networkError.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: errorMessage,
                });
            }
        },
        closeModal() {
            this.showCreateModal = false;
            this.editingNews = null;
        },
        closeViewModal() {
            this.viewingNews = null;
        },
        handleNewsSaved() {
            this.closeModal();
            this.loadNews();
            this.loadStatistics();
        },
        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
        },
        resolveThumbnailUrl(path) {
            if (!path) {
                return '';
            }
            if (typeof path === 'string' && (path.indexOf('http://') === 0 || path.indexOf('https://') === 0)) {
                return path;
            }
            const baseUrl = process.env.VUE_APP_PUBLIC_URL || '';
            return `${baseUrl}${path}`;
        },
        mapEntityName(entity) {
            return entity === 'ATHS' ? 'ATS' : entity;
        },
        getEntityTileColor(entity) {
            const colorMap = {
                'FCHS': 'bg-gradient-to-r from-blue-500 to-blue-600 text-white',
                'ADPOLY': 'bg-gradient-to-r from-purple-500 to-purple-600 text-white',
                'ATHS': 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white',
                'ADVETI': 'bg-gradient-to-r from-pink-500 to-pink-600 text-white',
            };
            return colorMap[entity] || 'bg-gradient-to-r from-orange-500 to-orange-600 text-white';
        },
        async togglePublish(news) {
            const action = news.is_published ? 'unpublish' : 'publish';
            const confirmText = news.is_published
                ? `Are you sure you want to unpublish "${news.title}"?`
                : `Are you sure you want to publish "${news.title}"?`;

            const result = await this.$swal.fire({
                title: 'Confirm',
                text: confirmText,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: news.is_published ? '#d33' : '#28a745',
                cancelButtonColor: '#6c757d',
                confirmButtonText: `Yes, ${action} it!`,
                cancelButtonText: 'Cancel'
            });

            if (!result.isConfirmed) {
                return;
            }

            try {
                const { data } = await this.$apollo.mutate({
                    mutation: gql`
                        mutation PublishNews($id: ID!, $publish: Boolean!) {
                            publishNews(id: $id, publish: $publish) {
                                error
                                error_code
                                message
                                data {
                                    id
                                    is_published
                                    published_at
                                    published_by
                                    publisher {
                                        id
                                        name
                                    }
                                }
                            }
                        }
                    `,
                    variables: {
                        id: news.id,
                        publish: !news.is_published,
                    },
                });

                if (data.publishNews.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Failed!',
                        text: data.publishNews.message || 'Failed to update publish status',
                    });
                } else {
                    this.$swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success',
                        title: `News ${action}ed successfully`,
                        showConfirmButton: false,
                        timer: 2000,
                        timerProgressBar: true,
                    });
                    this.loadNews();
                    if (this.viewingNews && this.viewingNews.id === news.id) {
                        this.viewingNews = { ...this.viewingNews, ...data.publishNews.data };
                    }
                }
            } catch (error) {
                console.error('Publish news error:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: 'Failed to update publish status',
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
